import { TFunction } from 'i18next';
import { IntegrationConfigBase } from 'models/integrations/integrationConfigBase';
import { IIntegrationEvidence } from 'models/integrations/integrationEvidence';
import { toLocaleDateTime } from 'utils/datetime';

//
// A HubSpot user or subscription type the customer exempted, shown by name in the config panel
//
export class HubSpotExemptionInfo {
  id: string = '';

  name: string = '';

  email: string = '';
}

//Copies a stored exemption list so a stale shape never reaches the config panels.
export const readExemptions = (raw: unknown): HubSpotExemptionInfo[] => {
  if (!Array.isArray(raw)) return [];

  return raw.map((r) => {
    const info = new HubSpotExemptionInfo();
    info.id = r?.id ?? '';
    info.name = r?.name ?? '';
    info.email = r?.email ?? '';

    return info;
  });
};

//The ids of two exemption lists are equal regardless of order: the dirty check of the config panels.
export const hasSameExemptionIds = (a: HubSpotExemptionInfo[], b: HubSpotExemptionInfo[]): boolean => {
  if (a.length !== b.length) return false;

  return a.every((x) => b.some((y) => y.id === x.id));
};

//
// Config classes
//
export class IntegrationConfig_HubSpot_InactiveUsers extends IntegrationConfigBase {
  inactiveDays: number = 45;

  exemptUsers: HubSpotExemptionInfo[] = [];

  createChecklistItemsPerUser: boolean = false;
}

export class IntegrationConfig_HubSpot_DataHosting extends IntegrationConfigBase {
  allowedLocations: string[] = ['eu1'];
}

export class IntegrationConfig_HubSpot_SuperAdmins extends IntegrationConfigBase {
  maxSuperAdmins: number = 2;

  exemptUsers: HubSpotExemptionInfo[] = [];
}

export class IntegrationConfig_HubSpot_LoginCountries extends IntegrationConfigBase {
  allowedCountries: string[] = ['NL', 'BE', 'DE'];

  lookbackDays: number = 30;

  exemptUsers: HubSpotExemptionInfo[] = [];
}

//
// Evidence: HubSpot_InactiveUsers
//
export class HubSpotInactiveUser {
  id: string = '';

  name: string = '';

  email: string = '';

  //undefined when the user never logged in during the 90 days HubSpot keeps
  lastLoginUtc: Date | undefined = undefined;
}

export class HubSpotInactiveUsersEvidence implements IIntegrationEvidence {
  isCapped: boolean = false;

  evaluatedAtUtc: Date = new Date();

  totalUserCount: number = 0;

  inactiveDays: number = 45;

  totalInactive: number = 0;

  findings: HubSpotInactiveUser[] = [];

  exemptions: HubSpotInactiveUser[] = [];

  pass: boolean = false;

  toMarkdownReport(t: TFunction<['translation', 'integrations']>): string {
    const sb: string[] = [];

    sb.push(
      `## ${t('integrations:HubSpot_InactiveUsers.Report.SummaryHeading', {
        date: toLocaleDateTime(this.evaluatedAtUtc),
      })}`,
    );
    sb.push(
      `- ${t('integrations:HubSpot_InactiveUsers.Report.UsersLine', {
        inactive: this.totalInactive,
        total: this.totalUserCount,
        days: this.inactiveDays,
      })}`,
    );
    this.findings.forEach((f) => {
      sb.push(
        `- ${t('integrations:HubSpot_InactiveUsers.Report.FindingLine', {
          name: f.name,
          email: f.email,
          lastLogin: f.lastLoginUtc
            ? toLocaleDateTime(f.lastLoginUtc)
            : t('integrations:HubSpot_InactiveUsers.Config.NoLogin'),
        })}`,
      );
    });
    sb.push(
      `- ${t('integrations:HubSpot_InactiveUsers.Report.OutcomeLine', {
        outcome: this.pass
          ? t('integrations:HubSpot_InactiveUsers.Result.Pass')
          : t('integrations:HubSpot_InactiveUsers.Result.Fail'),
      })}`,
    );

    return sb.join('\n');
  }
}

const parseInactiveUsers = (raw: unknown): HubSpotInactiveUser[] => {
  if (!Array.isArray(raw)) return [];

  return raw.map((r) => {
    const user = new HubSpotInactiveUser();
    user.id = r?.id ?? '';
    user.name = r?.name ?? '';
    user.email = r?.email ?? '';
    user.lastLoginUtc = r?.lastLoginUtc ? new Date(r.lastLoginUtc) : undefined;

    return user;
  });
};

export const parse_Evidence_HubSpot_InactiveUsers = (
  raw: string | undefined,
): HubSpotInactiveUsersEvidence | undefined => {
  if (!raw) return undefined;

  const rawEvidence = JSON.parse(raw);
  if (!rawEvidence) return undefined;

  const evidence = new HubSpotInactiveUsersEvidence();
  evidence.isCapped = rawEvidence.isCapped ?? false;
  evidence.evaluatedAtUtc = rawEvidence.evaluatedAtUtc ? new Date(rawEvidence.evaluatedAtUtc) : new Date();
  evidence.totalUserCount = rawEvidence.totalUserCount ?? 0;
  evidence.inactiveDays = rawEvidence.inactiveDays ?? 45;
  evidence.totalInactive = rawEvidence.totalInactive ?? 0;
  evidence.findings = parseInactiveUsers(rawEvidence.findings);
  evidence.exemptions = parseInactiveUsers(rawEvidence.exemptions);
  evidence.pass = rawEvidence.pass ?? false;

  return evidence;
};

//
// Evidence: HubSpot_DataHosting
//
export class HubSpotDataHostingEvidence implements IIntegrationEvidence {
  isCapped: boolean = false;

  evaluatedAtUtc: Date = new Date();

  portalId: number = 0;

  accountType: string = '';

  //the resolved data center code, for example eu1 or na1
  dataHostingLocation: string = '';

  uiDomain: string = '';

  allowedLocations: string[] = [];

  pass: boolean = false;

  toMarkdownReport(t: TFunction<['translation', 'integrations']>): string {
    const sb: string[] = [];

    sb.push(
      `## ${t('integrations:HubSpot_DataHosting.Report.SummaryHeading', {
        date: toLocaleDateTime(this.evaluatedAtUtc),
      })}`,
    );
    sb.push(
      `- ${t('integrations:HubSpot_DataHosting.Report.LocationLine', {
        location: this.dataHostingLocation,
        allowed: this.allowedLocations.join(', '),
      })}`,
    );
    sb.push(
      `- ${t('integrations:HubSpot_DataHosting.Report.OutcomeLine', {
        outcome: this.pass
          ? t('integrations:HubSpot_DataHosting.Result.Pass')
          : t('integrations:HubSpot_DataHosting.Result.Fail'),
      })}`,
    );

    return sb.join('\n');
  }
}

export const parse_Evidence_HubSpot_DataHosting = (raw: string | undefined): HubSpotDataHostingEvidence | undefined => {
  if (!raw) return undefined;

  const rawEvidence = JSON.parse(raw);
  if (!rawEvidence) return undefined;

  const evidence = new HubSpotDataHostingEvidence();
  evidence.isCapped = rawEvidence.isCapped ?? false;
  evidence.evaluatedAtUtc = rawEvidence.evaluatedAtUtc ? new Date(rawEvidence.evaluatedAtUtc) : new Date();
  evidence.portalId = rawEvidence.portalId ?? 0;
  evidence.accountType = rawEvidence.accountType ?? '';
  evidence.dataHostingLocation = rawEvidence.dataHostingLocation ?? '';
  evidence.uiDomain = rawEvidence.uiDomain ?? '';
  evidence.allowedLocations = Array.isArray(rawEvidence.allowedLocations) ? rawEvidence.allowedLocations : [];
  evidence.pass = rawEvidence.pass ?? false;

  return evidence;
};

//
// Evidence: HubSpot_SuperAdmins
//
export class HubSpotSuperAdmin {
  id: string = '';

  name: string = '';

  email: string = '';

  isExempt: boolean = false;
}

export class HubSpotSuperAdminsEvidence implements IIntegrationEvidence {
  isCapped: boolean = false;

  evaluatedAtUtc: Date = new Date();

  totalUserCount: number = 0;

  maxSuperAdmins: number = 2;

  countedSuperAdmins: number = 0;

  superAdmins: HubSpotSuperAdmin[] = [];

  pass: boolean = false;

  toMarkdownReport(t: TFunction<['translation', 'integrations']>): string {
    const sb: string[] = [];

    sb.push(
      `## ${t('integrations:HubSpot_SuperAdmins.Report.SummaryHeading', {
        date: toLocaleDateTime(this.evaluatedAtUtc),
      })}`,
    );
    sb.push(
      `- ${t('integrations:HubSpot_SuperAdmins.Report.CountLine', {
        counted: this.countedSuperAdmins,
        max: this.maxSuperAdmins,
      })}`,
    );
    this.superAdmins.forEach((a) => {
      sb.push(
        `- ${t('integrations:HubSpot_SuperAdmins.Report.AdminLine', {
          name: a.name,
          email: a.email,
          exempt: a.isExempt ? t('translation:General.Words.Yes') : t('translation:General.Words.No'),
        })}`,
      );
    });
    sb.push(
      `- ${t('integrations:HubSpot_SuperAdmins.Report.OutcomeLine', {
        outcome: this.pass
          ? t('integrations:HubSpot_SuperAdmins.Result.Pass')
          : t('integrations:HubSpot_SuperAdmins.Result.Fail'),
      })}`,
    );

    return sb.join('\n');
  }
}

export const parse_Evidence_HubSpot_SuperAdmins = (raw: string | undefined): HubSpotSuperAdminsEvidence | undefined => {
  if (!raw) return undefined;

  const rawEvidence = JSON.parse(raw);
  if (!rawEvidence) return undefined;

  const evidence = new HubSpotSuperAdminsEvidence();
  evidence.isCapped = rawEvidence.isCapped ?? false;
  evidence.evaluatedAtUtc = rawEvidence.evaluatedAtUtc ? new Date(rawEvidence.evaluatedAtUtc) : new Date();
  evidence.totalUserCount = rawEvidence.totalUserCount ?? 0;
  evidence.maxSuperAdmins = rawEvidence.maxSuperAdmins ?? 2;
  evidence.countedSuperAdmins = rawEvidence.countedSuperAdmins ?? 0;
  evidence.superAdmins = Array.isArray(rawEvidence.superAdmins)
    ? rawEvidence.superAdmins.map((r: Partial<HubSpotSuperAdmin>) => {
        const admin = new HubSpotSuperAdmin();
        admin.id = r?.id ?? '';
        admin.name = r?.name ?? '';
        admin.email = r?.email ?? '';
        admin.isExempt = r?.isExempt ?? false;

        return admin;
      })
    : [];
  evidence.pass = rawEvidence.pass ?? false;

  return evidence;
};

//
// Evidence: HubSpot_LoginCountries
//
export class HubSpotLoginCountryFinding {
  userId: string = '';

  name: string = '';

  email: string = '';

  //ISO 3166-1 alpha-2, upper-case
  countryCode: string = '';

  loginCount: number = 0;

  lastLoginUtc: Date | undefined = undefined;
}

export class HubSpotLoginCountriesEvidence implements IIntegrationEvidence {
  isCapped: boolean = false;

  evaluatedAtUtc: Date = new Date();

  lookbackDays: number = 30;

  allowedCountries: string[] = [];

  totalSuccessfulLogins: number = 0;

  loginsWithoutCountry: number = 0;

  totalFindings: number = 0;

  findings: HubSpotLoginCountryFinding[] = [];

  exemptions: HubSpotLoginCountryFinding[] = [];

  pass: boolean = false;

  toMarkdownReport(t: TFunction<['translation', 'integrations']>): string {
    const sb: string[] = [];

    sb.push(
      `## ${t('integrations:HubSpot_LoginCountries.Report.SummaryHeading', {
        date: toLocaleDateTime(this.evaluatedAtUtc),
      })}`,
    );
    sb.push(
      `- ${t('integrations:HubSpot_LoginCountries.Report.SummaryLine', {
        number: this.totalFindings,
        total: this.totalSuccessfulLogins,
        days: this.lookbackDays,
        allowed: this.allowedCountries.join(', '),
      })}`,
    );
    this.findings.forEach((f) => {
      sb.push(
        `- ${t('integrations:HubSpot_LoginCountries.Report.FindingLine', {
          name: f.name,
          email: f.email,
          country: f.countryCode,
          logins: f.loginCount,
          lastLogin: f.lastLoginUtc ? toLocaleDateTime(f.lastLoginUtc) : '',
        })}`,
      );
    });
    sb.push(
      `- ${t('integrations:HubSpot_LoginCountries.Report.OutcomeLine', {
        outcome: this.pass
          ? t('integrations:HubSpot_LoginCountries.Result.Pass')
          : t('integrations:HubSpot_LoginCountries.Result.Fail'),
      })}`,
    );

    return sb.join('\n');
  }
}

const parseLoginCountryFindings = (raw: unknown): HubSpotLoginCountryFinding[] => {
  if (!Array.isArray(raw)) return [];

  return raw.map((r) => {
    const finding = new HubSpotLoginCountryFinding();
    finding.userId = r?.userId ?? '';
    finding.name = r?.name ?? '';
    finding.email = r?.email ?? '';
    finding.countryCode = r?.countryCode ?? '';
    finding.loginCount = r?.loginCount ?? 0;
    finding.lastLoginUtc = r?.lastLoginUtc ? new Date(r.lastLoginUtc) : undefined;

    return finding;
  });
};

export const parse_Evidence_HubSpot_LoginCountries = (
  raw: string | undefined,
): HubSpotLoginCountriesEvidence | undefined => {
  if (!raw) return undefined;

  const rawEvidence = JSON.parse(raw);
  if (!rawEvidence) return undefined;

  const evidence = new HubSpotLoginCountriesEvidence();
  evidence.isCapped = rawEvidence.isCapped ?? false;
  evidence.evaluatedAtUtc = rawEvidence.evaluatedAtUtc ? new Date(rawEvidence.evaluatedAtUtc) : new Date();
  evidence.lookbackDays = rawEvidence.lookbackDays ?? 30;
  evidence.allowedCountries = Array.isArray(rawEvidence.allowedCountries) ? rawEvidence.allowedCountries : [];
  evidence.totalSuccessfulLogins = rawEvidence.totalSuccessfulLogins ?? 0;
  evidence.loginsWithoutCountry = rawEvidence.loginsWithoutCountry ?? 0;
  evidence.totalFindings = rawEvidence.totalFindings ?? 0;
  evidence.findings = parseLoginCountryFindings(rawEvidence.findings);
  evidence.exemptions = parseLoginCountryFindings(rawEvidence.exemptions);
  evidence.pass = rawEvidence.pass ?? false;

  return evidence;
};
